import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { CoachingVisibility, Player } from "@/lib/types";
import { CanvasSelect } from "@/features/canvas/components/CanvasSelect";
import {
  NOTE_CATEGORIES,
  NOTE_CATEGORY_LABELS,
} from "./Composer";
import { formatPlayhead } from "@/features/playback/useVideoPlayhead";

/**
 * Clip composer — the "Save Clip" form in Coach Review.
 *
 * A clip is a `[start_seconds, end_seconds]` window of a match's
 * video. Ports the legacy Replay clip composer
 * (`js/coaching/clips.js` + the `coach-clip-form-template`):
 *
 *   - Full field set: title, visibility, category, start, end,
 *     live duration label (red when the window is invalid), tagged
 *     players, description.
 *   - **Match + slot are NOT editable here.** A clip's match
 *     anchor is immutable — `UpdateCoachingClipRequest` is
 *     `extra="forbid"` and omits `match_id`/`slot`. The parent's
 *     `MatchSlotPicker` owns the match/slot selection; the clip
 *     composer just shows them read-only. On EDIT the parent locks
 *     the picker entirely.
 *   - `source_note_id` shows as a read-only "From note #N" pill
 *     when present. It's CREATE-only — never editable, never
 *     re-emitted on PATCH (rebinding the source would silently
 *     swap which note's drawing snapshot the clip captured).
 *   - Client-side window validation: `end > start` and
 *     `duration ≤ 120s` (`MAX_CLIP_DURATION_SECONDS`, mirrors the
 *     backend `_MAX_CLIP_DURATION_SECONDS`) — block submit before
 *     the backend 422s.
 *
 * Like `Composer`, exposes a `ClipComposerHandle` so the parent
 * can read live values (focus-mode snapshot continuity) and reset
 * after a save.
 */

/** Mirrors the backend `_MAX_CLIP_DURATION_SECONDS` in `models.py`. */
export const MAX_CLIP_DURATION_SECONDS = 120;

const VISIBILITY_OPTIONS: ReadonlyArray<{
  value: CoachingVisibility;
  label: string;
  description: string;
}> = [
  { value: "private", label: "Coach-only (private)", description: "Only coaches see this." },
  { value: "team", label: "Team", description: "All team members + linked viewers." },
  { value: "player", label: "Player + family", description: "Only tagged players + their family accounts." },
  { value: "unlisted", label: "Unlisted", description: "Direct link only — not in the team feed." },
];

export const clipComposerSchema = z
  .object({
    title: z.string().min(1, "Title is required").max(200),
    description: z.string().max(4000),
    category: z.enum(NOTE_CATEGORIES),
    visibility: z.enum(["private", "team", "player", "unlisted"]),
    // Seconds. The number `<input>`s register with
    // `valueAsNumber: true` so RHF hands the resolver real numbers
    // — `z.coerce.number()` here would make the schema's INPUT type
    // `unknown`, which trips RHF's resolver generics (same gotcha
    // the note Composer's schema docstring calls out).
    start_seconds: z.number().min(0, "Start must be ≥ 0"),
    end_seconds: z.number().min(0, "End must be ≥ 0"),
    player_ids: z.array(z.string()),
  })
  .refine((v) => v.end_seconds > v.start_seconds, {
    message: "End must be after start",
    path: ["end_seconds"],
  })
  .refine(
    (v) => v.end_seconds - v.start_seconds <= MAX_CLIP_DURATION_SECONDS,
    {
      message: `Clip must be ${MAX_CLIP_DURATION_SECONDS}s or shorter`,
      path: ["end_seconds"],
    },
  );

export type ClipComposerValues = z.infer<typeof clipComposerSchema>;

export type ClipComposerHandle = {
  getValues: () => ClipComposerValues;
  reset: (next?: Partial<ClipComposerValues>) => void;
};

export type ClipComposerProps = {
  defaultValues?: Partial<ClipComposerValues>;
  players: Player[];
  onSubmit: SubmitHandler<ClipComposerValues>;
  submitLabel?: string;
  /** Match label shown read-only above the window inputs. The
   * picker that drives it lives in the parent. */
  matchLabel: string;
  /** Slot shown read-only beside the match. */
  slot: string;
  /** When true, the clip is being edited — the match/slot are
   * fixed (the parent also locks its picker). Purely a hint for
   * the read-only banner copy; the composer never emits
   * match_id/slot regardless. */
  isEditing?: boolean;
  /** Source note id, when the clip was derived from a timeline
   * chip / co-located note. Read-only — shown as a "From note #N"
   * pill. */
  sourceNoteId?: number | null;
};

const DEFAULTS: ClipComposerValues = {
  title: "",
  description: "",
  category: "other",
  visibility: "team",
  start_seconds: 0,
  end_seconds: 0,
  player_ids: [],
};

export const ClipComposer = forwardRef<ClipComposerHandle, ClipComposerProps>(
  function ClipComposer(
    {
      defaultValues,
      players,
      onSubmit,
      submitLabel = "Save clip",
      matchLabel,
      slot,
      isEditing = false,
      sourceNoteId,
    }: ClipComposerProps,
    ref,
  ) {
    const form = useForm<ClipComposerValues>({
      resolver: zodResolver(clipComposerSchema),
      defaultValues: { ...DEFAULTS, ...defaultValues },
    });

    useEffect(() => {
      form.reset({ ...DEFAULTS, ...defaultValues });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(defaultValues)]);

    useImperativeHandle(
      ref,
      () => ({
        getValues: () => form.getValues(),
        reset: (next?: Partial<ClipComposerValues>) =>
          form.reset({ ...DEFAULTS, ...(next ?? {}) }),
      }),
      [form],
    );

    const start = Number(form.watch("start_seconds"));
    const end = Number(form.watch("end_seconds"));
    const visibility = form.watch("visibility");
    const activeVisibility = VISIBILITY_OPTIONS.find(
      (v) => v.value === visibility,
    );

    // Live duration label — red when the window is degenerate or
    // over the 120s cap. Mirrors the legacy composer's live label.
    const duration = useMemo(() => {
      if (!Number.isFinite(start) || !Number.isFinite(end)) {
        return { seconds: NaN, valid: false };
      }
      const d = end - start;
      return {
        seconds: d,
        valid: d > 0 && d <= MAX_CLIP_DURATION_SECONDS,
      };
    }, [start, end]);

    return (
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
        data-testid="coach-review-clip-composer"
      >
        {/* Read-only match anchor. The clip's match/slot are owned
         * by the parent's picker — immutable once the clip exists
         * (`UpdateCoachingClipRequest` is `extra="forbid"`). */}
        <div
          className="rounded border border-rule bg-bg-card px-3 py-2 text-xs"
          data-testid="clip-composer-match-anchor"
        >
          <span className="text-ink-muted uppercase tracking-wider font-display">
            Match
          </span>
          <div className="text-ink mt-0.5">
            {matchLabel} · {slot}
          </div>
          {isEditing ? (
            <div className="text-ink-muted mt-1">
              Match + slot are fixed on edit.
            </div>
          ) : null}
        </div>

        {sourceNoteId != null ? (
          <div
            className="self-start rounded-full border border-accent/40 bg-accent/10 px-2.5 py-1 text-xs text-ink"
            data-testid="clip-composer-source-note"
          >
            From note #{sourceNoteId}
          </div>
        ) : null}

        <Field label="Title" error={form.formState.errors.title?.message}>
          <input
            type="text"
            {...form.register("title")}
            data-testid="clip-composer-title"
            className="w-full bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Start (seconds)"
            error={form.formState.errors.start_seconds?.message}
          >
            <input
              type="number"
              step="0.1"
              min="0"
              {...form.register("start_seconds", { valueAsNumber: true })}
              data-testid="clip-composer-start"
              className="w-full bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </Field>
          <Field
            label="End (seconds)"
            error={form.formState.errors.end_seconds?.message}
          >
            <input
              type="number"
              step="0.1"
              min="0"
              {...form.register("end_seconds", { valueAsNumber: true })}
              data-testid="clip-composer-end"
              className="w-full bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </Field>
        </div>

        {/* Live duration label — red when the window is invalid. */}
        <p
          className={`text-xs font-mono ${
            duration.valid ? "text-ink-muted" : "text-destructive"
          }`}
          data-testid="clip-composer-duration"
        >
          Duration:{" "}
          {Number.isFinite(duration.seconds)
            ? `${duration.seconds.toFixed(1)}s`
            : "—"}
          {Number.isFinite(duration.seconds) && duration.seconds > 0
            ? ` (${formatPlayhead(start)} → ${formatPlayhead(end)})`
            : ""}
          {!duration.valid
            ? duration.seconds > MAX_CLIP_DURATION_SECONDS
              ? ` · over the ${MAX_CLIP_DURATION_SECONDS}s cap`
              : " · end must be after start"
            : ""}
        </p>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <CanvasSelect
              {...form.register("category")}
              data-testid="clip-composer-category"
            >
              {NOTE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {NOTE_CATEGORY_LABELS[c]}
                </option>
              ))}
            </CanvasSelect>
          </Field>
          <Field label="Visibility" helper={activeVisibility?.description}>
            <CanvasSelect
              {...form.register("visibility")}
              data-testid="clip-composer-visibility"
            >
              {VISIBILITY_OPTIONS.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </CanvasSelect>
          </Field>
        </div>

        <Field label="Tag players" helper="Hold ⌘ / Ctrl to select multiple">
          <CanvasSelect
            multiple
            size={Math.min(6, Math.max(3, players.length))}
            value={form.watch("player_ids")}
            onChange={(e) => {
              const selected = Array.from(
                e.currentTarget.selectedOptions,
                (o) => o.value,
              );
              form.setValue("player_ids", selected, { shouldDirty: true });
            }}
            data-testid="clip-composer-player-ids"
            className="!py-0"
          >
            {players.map((p) => (
              <option key={p.id} value={p.id}>
                {p.display_name}
                {p.jersey_number ? ` · #${p.jersey_number}` : ""}
              </option>
            ))}
          </CanvasSelect>
        </Field>

        <Field
          label="Description"
          error={form.formState.errors.description?.message}
        >
          <textarea
            {...form.register("description")}
            data-testid="clip-composer-description"
            rows={3}
            className="w-full bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </Field>

        <button
          type="submit"
          data-testid="clip-composer-submit"
          disabled={form.formState.isSubmitting}
          className="bg-accent hover:bg-accent-hover disabled:bg-bg-card disabled:text-ink-muted text-white rounded px-4 py-2 text-sm font-display uppercase tracking-wide transition-colors"
        >
          {form.formState.isSubmitting ? "Saving…" : submitLabel}
        </button>
      </form>
    );
  },
);

/** Field wrapper — label + optional helper + error. Mirrors the
 * one in `Composer.tsx` (kept local to avoid an export just for
 * this; if a third composer appears, lift it to a shared file). */
function Field(props: {
  label: string;
  helper?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {props.label}
      </span>
      {props.children}
      {props.helper ? (
        <span className="text-xs text-ink-muted">{props.helper}</span>
      ) : null}
      {props.error ? (
        <span className="text-xs text-destructive">{props.error}</span>
      ) : null}
    </label>
  );
}
